import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { BRANCHES } from '@/mocks/mockData';

interface Student { id: string; ogrenci_adi: string; veli_adi: string | null; telefon: string | null; sinif: string | null; branslar: string[] | null; ders_tipi: string | null; notlar: string | null; }
interface Teacher { id: string; ad_soyad: string; branslar: string[] | null; musaitlik_notu: string | null; varsayilan_ders_ucreti: number | null; telefon: string | null; }

function dbToLessonType(db: string | null): string {
  switch (db) {
    case 'bireysel_online': return 'Birebir Online';
    case 'yuz_yuze': return 'Yüz Yüze';
    case 'grup': return 'Grup Dersi';
    default: return db || '—';
  }
}

const emptyForm = {
  studentId: '', branch: '', teacherId: '', trialDate: '', trialTime: '',
  trialDuration: '40 dk' as '20 dk' | '40 dk' | 'Diğer', teacherNote: '', status: 'Deneme Dersi Planlandı',
};

export default function YonlendirmePage() {
  const [form, setForm] = useState(emptyForm);
  const [students, setStudents] = useState<Student[]>([]);
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showWAModal, setShowWAModal] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      const [{ data: s }, { data: t }] = await Promise.all([
        supabase.from('egtpanel_ogrenciler').select('id, ogrenci_adi, veli_adi, telefon, sinif, branslar, ders_tipi, notlar').eq('aktif', true).order('ogrenci_adi'),
        supabase.from('egtpanel_ogretmenler').select('id, ad_soyad, branslar, musaitlik_notu, varsayilan_ders_ucreti, telefon').eq('aktif', true).order('ad_soyad'),
      ]);
      setStudents(s || []);
      setTeachers(t || []);
      setLoading(false);
    };
    fetchData();
  }, []);

  const selectedStudent = students.find(s => s.id === form.studentId);
  const selectedTeacher = teachers.find(t => t.id === form.teacherId);

  const availableTeachers = teachers.filter(t => {
    if (!form.branch) return true;
    return t.branslar?.includes(form.branch);
  });

  const waMessage = selectedStudent && selectedTeacher
    ? `Merhaba ${selectedTeacher.ad_soyad.split(' ')[0]} Hocam,\n\nDersegel Antalya üzerinden size yeni bir öğrenci yönlendirildi.\n\n👤 Öğrenci: ${selectedStudent.ogrenci_adi}\n📚 Sınıf: ${selectedStudent.sinif || 'Belirtilmedi'}\n📖 Ders: ${form.branch || (selectedStudent.branslar?.[0] ?? '')}\n📅 Deneme Tarihi: ${form.trialDate || 'Belirtilmedi'} ${form.trialTime || ''}\n⏱ Süre: ${form.trialDuration}\n\n${form.teacherNote ? `📝 Not: ${form.teacherNote}\n\n` : ''}Uygun olduğunuzda tanışma/deneme dersi için iletişime geçebilirsiniz.\n\nİyi çalışmalar! 🎓\nDersegel Antalya Operasyon Ekibi`
    : '';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.studentId || !form.teacherId) return;
    setSaving(true);
    await supabase.from('egtpanel_yonlendirmeler').insert({
      ogrenci_id: form.studentId,
      ogretmen_id: form.teacherId,
      brans: form.branch || null,
      deneme_tarihi: form.trialDate || null,
      deneme_saati: form.trialTime || null,
      deneme_suresi: form.trialDuration,
      ogretmene_not: form.teacherNote || null,
      durum: form.status,
      whatsapp_mesaj_gonderildi: false,
    });
    setSaving(false);
    setSuccess(true);
    setTimeout(() => setSuccess(false), 3000);
    setForm(emptyForm);
  };

  if (loading) return (
    <div className="p-6 flex items-center justify-center py-32">
      <i className="ri-loader-4-line text-3xl text-slate-300 animate-spin" />
    </div>
  );

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-800">Öğrenci Yönlendirme</h1>
        <p className="text-slate-500 text-sm mt-1">Öğrencileri uygun öğretmenlere yönlendirin ve deneme dersini planlayın</p>
      </div>

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3">
          <i className="ri-checkbox-circle-line text-green-600 text-xl" />
          <p className="text-green-800 font-medium text-sm">Öğrenci başarıyla yönlendirildi!</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: Student Info */}
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-slate-100 p-5">
            <h2 className="text-base font-semibold text-slate-700 mb-4 flex items-center gap-2">
              <i className="ri-user-line text-[#1e3a5f]" /> Öğrenci Bilgileri
            </h2>
            <div className="mb-4">
              <label className="block text-xs font-medium text-slate-600 mb-1">Öğrenci Seç *</label>
              <select value={form.studentId} onChange={e => setForm(p => ({...p, studentId: e.target.value, branch: ''}))}
                className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20">
                <option value="">— Öğrenci seçiniz —</option>
                {students.map(s => (
                  <option key={s.id} value={s.id}>{s.ogrenci_adi} {s.sinif ? `— ${s.sinif}` : ''}</option>
                ))}
              </select>
            </div>

            {selectedStudent ? (
              <div className="space-y-2">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#1e3a5f] text-white font-bold text-lg">
                    {selectedStudent.ogrenci_adi.charAt(0)}
                  </div>
                  <div>
                    <div className="font-semibold text-slate-800">{selectedStudent.ogrenci_adi}</div>
                    <div className="text-sm text-slate-500">{selectedStudent.sinif || '—'}</div>
                  </div>
                </div>
                {[
                  { label: 'Veli', val: selectedStudent.veli_adi || '—' },
                  { label: 'Telefon', val: selectedStudent.telefon || '—' },
                  { label: 'Branşlar', val: selectedStudent.branslar?.join(', ') || '—' },
                  { label: 'Ders Tipi', val: dbToLessonType(selectedStudent.ders_tipi) },
                ].map(r => (
                  <div key={r.label} className="flex gap-3 py-1.5 border-b border-slate-50 text-sm">
                    <span className="text-slate-400 w-20 shrink-0">{r.label}</span>
                    <span className="text-slate-700 font-medium">{r.val}</span>
                  </div>
                ))}
                {selectedStudent.notlar && (
                  <div className="bg-amber-50 rounded-lg p-3 text-xs text-amber-800 mt-2">
                    <i className="ri-sticky-note-line mr-1" /> {selectedStudent.notlar}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8">
                <i className="ri-user-search-line text-4xl text-slate-200 block mb-2" />
                <p className="text-slate-400 text-sm">Öğrenci seçmek için yukarıdaki alanı kullanın</p>
              </div>
            )}
          </div>

          <div className="bg-white rounded-xl border border-slate-100 p-5">
            <label className="block text-xs font-medium text-slate-600 mb-2">Yönlendirilecek Branş *</label>
            <div className="flex flex-wrap gap-2">
              {(selectedStudent?.branslar?.length ? selectedStudent.branslar : BRANCHES).map(b => (
                <button key={b} type="button" onClick={() => setForm(p => ({...p, branch: b}))}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors cursor-pointer ${form.branch === b ? 'bg-[#1e3a5f] text-white border-[#1e3a5f]' : 'bg-white text-slate-600 border-slate-200 hover:border-[#1e3a5f]/40'}`}>
                  {b}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Right */}
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-slate-100 p-5">
            <h2 className="text-base font-semibold text-slate-700 mb-4 flex items-center gap-2">
              <i className="ri-user-star-line text-[#1e3a5f]" /> Öğretmen Seçimi
            </h2>
            {availableTeachers.length === 0 ? (
              <div className="text-center py-8">
                <i className="ri-user-unfollow-line text-4xl text-slate-200 block mb-2" />
                <p className="text-slate-400 text-sm">Bu branş için uygun öğretmen bulunamadı</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                {availableTeachers.map(t => (
                  <div key={t.id} onClick={() => setForm(p => ({...p, teacherId: t.id}))}
                    className={`flex items-center justify-between p-3 rounded-xl border cursor-pointer transition-all ${form.teacherId === t.id ? 'border-[#1e3a5f] bg-[#1e3a5f]/5' : 'border-slate-100 hover:border-slate-200 hover:bg-slate-50'}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#1e3a5f] text-white text-sm font-bold shrink-0">
                        {t.ad_soyad.charAt(0)}
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-slate-800">{t.ad_soyad}</div>
                        <div className="text-xs text-slate-400">{t.branslar?.slice(0, 2).join(', ') || '—'}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      {t.varsayilan_ders_ucreti && <div className="text-xs text-slate-400">₺{t.varsayilan_ders_ucreti}/ders</div>}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {selectedTeacher && (
              <div className="mt-4 bg-[#1e3a5f]/5 rounded-xl p-3">
                <div className="text-xs font-semibold text-[#1e3a5f] mb-1">Seçilen Öğretmen</div>
                <div className="text-sm font-medium text-slate-800">{selectedTeacher.ad_soyad}</div>
                {selectedTeacher.musaitlik_notu && <div className="text-xs text-slate-500">{selectedTeacher.musaitlik_notu}</div>}
              </div>
            )}
          </div>

          <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-slate-100 p-5 space-y-4">
            <h2 className="text-base font-semibold text-slate-700 flex items-center gap-2">
              <i className="ri-calendar-check-line text-[#1e3a5f]" /> Deneme Dersi Planla
            </h2>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Deneme Tarihi</label>
                <input type="date" value={form.trialDate} onChange={e => setForm(p => ({...p, trialDate: e.target.value}))} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Saat</label>
                <input type="time" value={form.trialTime} onChange={e => setForm(p => ({...p, trialTime: e.target.value}))} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Beklenen Süre</label>
              <div className="flex gap-2">
                {(['20 dk', '40 dk', 'Diğer'] as const).map(d => (
                  <button key={d} type="button" onClick={() => setForm(p => ({...p, trialDuration: d}))}
                    className={`flex-1 py-2 rounded-lg text-xs font-medium border transition-colors cursor-pointer ${form.trialDuration === d ? 'bg-[#1e3a5f] text-white border-[#1e3a5f]' : 'text-slate-600 border-slate-200 hover:border-slate-300'}`}>
                    {d}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Durum</label>
              <select value={form.status} onChange={e => setForm(p => ({...p, status: e.target.value}))} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20">
                {['Deneme Dersi Planlandı', 'Öğretmene Yönlendirildi', 'Takipte'].map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Öğretmene Özel Not</label>
              <textarea value={form.teacherNote} onChange={e => setForm(p => ({...p, teacherNote: e.target.value}))} rows={3} maxLength={500} placeholder="Öğrenci hakkında öğretmene bilgi notunuz..." className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20 resize-none" />
            </div>
            <div className="flex gap-3">
              <button type="button" onClick={() => { if (selectedStudent && selectedTeacher) setShowWAModal(true); }}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium border transition-colors ${selectedStudent && selectedTeacher ? 'bg-green-500 text-white border-green-500 hover:bg-green-600 cursor-pointer' : 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed'}`}>
                <i className="ri-whatsapp-line" /> WhatsApp Mesajı
              </button>
              <button type="submit" disabled={!form.studentId || !form.teacherId || saving}
                className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-colors ${form.studentId && form.teacherId && !saving ? 'bg-[#1e3a5f] text-white hover:bg-[#162d4a] cursor-pointer' : 'bg-slate-100 text-slate-400 cursor-not-allowed'}`}>
                {saving ? <i className="ri-loader-4-line animate-spin" /> : 'Yönlendir'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* WhatsApp Modal */}
      {showWAModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg">
            <div className="flex items-center justify-between p-6 border-b border-slate-100">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-green-500 text-white">
                  <i className="ri-whatsapp-line" />
                </div>
                <h2 className="text-lg font-bold text-slate-800">WhatsApp Mesajı</h2>
              </div>
              <button onClick={() => setShowWAModal(false)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-400 cursor-pointer">
                <i className="ri-close-line" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="text-xs text-slate-500 font-medium">Gönderilecek: {selectedTeacher?.telefon || '—'}</div>
              <div className="bg-green-50 rounded-xl p-4">
                <pre className="text-sm text-slate-800 whitespace-pre-wrap font-sans leading-relaxed">{waMessage}</pre>
              </div>
              <div className="flex gap-3">
                <button onClick={() => navigator.clipboard.writeText(waMessage)} className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50 transition-colors cursor-pointer">
                  <i className="ri-clipboard-line" /> Kopyala
                </button>
                <a href={`https://wa.me/${selectedTeacher?.telefon?.replace(/\D/g, '')}?text=${encodeURIComponent(waMessage)}`} target="_blank" rel="nofollow noreferrer"
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg bg-green-500 text-white text-sm font-medium hover:bg-green-600 transition-colors">
                  <i className="ri-whatsapp-line" /> WhatsApp&apos;ta Aç
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}